import { html, LitElement } from 'lit-element';
import { ifDefined } from 'lit-html/directives/if-defined';
import { ScopedElementsMixin } from '@open-wc/scoped-elements';
import { ibanFormat } from '@aab/data-formatters';
import AabAmount from '@aab/sc-aab-amount/src/AabAmount';
import AabAvatar from '@aab/sc-aab-avatar/dist/AabAvatar';
import AabCheckbox from '@aab/sc-aab-checkbox/dist/AabCheckbox';
import AabIcon from '@aab/sc-aab-icon/src/AabIcon';
import AabInputMessage from '@aab/sc-aab-inputmessage/src/AabInputmessage';
import Styles from './styles.scss';

export default class AabTableCell extends ScopedElementsMixin(LitElement) {
  constructor() {
    super();
    this.data = '';
    this.template = '';
  }

  static get properties() {
    return {
      data: { type: Object, reflect: true },
      template: { type: String, reflect: true },
    };
  }

  static get scopedElements() {
    return {
      'aab-amount': AabAmount,
      'aab-avatar': AabAvatar,
      'aab-checkbox': AabCheckbox,
      'aab-icon': AabIcon,
      'aab-inputmessage': AabInputMessage,
    };
  }

  static get styles() {
    return [Styles];
  }

  static renderAccount({ name, type, number }) {
    return html`
      <div>
        <h4 class="m-0 account-name">${name}</h4>
        <div class="text-small">${type}</div>
        <div class="text-small">${ibanFormat(number)}</div>
      </div>`;
  }

  static renderAmount({
    amount, currency, locale, color, noFormatting, prefix, symbol,
  }) {
    return html`<aab-amount amount=${ifDefined(amount)} currency=${ifDefined(currency)} locale=${ifDefined(locale)}
      color=${ifDefined(color)} ?prefix=${prefix} ?symbol=${symbol} ?no-formatting=${noFormatting}></aab-amount>`;
  }

  static renderAvatar({
    name, icon, image, size, displayName,
  }) {
    const displayedName = displayName === true ? html`<div class="ml-2">${name}</div>` : '';
    return html`<aab-avatar name=${ifDefined(name)} icon=${ifDefined(icon)} image=${ifDefined(image)}
      size=${ifDefined(size)}></aab-avatar>${displayedName}`;
  }

  static renderCheckbox({ id, checked, label }) {
    return html`<aab-checkbox
      @click=${(e) => e.stopPropagation()}>
        <input slot="input-slot" type="checkbox" id=${ifDefined(id)} ?checked=${checked} />
        <label slot="label-slot" for=${ifDefined(id)}>${label}</label>
      </aab-checkbox>`;
  }

  static renderIcon({
    size, color, svg, action, data, label, role, arialabel,
  }) {
    let tabIndex;
    let click;
    let keypress;
    if (action) {
      tabIndex = '0';
      click = (evt) => AabTableCell.handleClick(evt, action, data);
      keypress = (evt) => AabTableCell.handleKeyPress(evt, action, data);
    }
    return html`<aab-icon
            class='smr-2 icon-spacing'
            size=${ifDefined(size)}
            color=${ifDefined(color)}
            svg=${ifDefined(svg)}
            label=${ifDefined(label)}
            role=${ifDefined(role)}
            tabindex=${ifDefined(tabIndex)}
            @click=${ifDefined(click)}
            @keypress=${ifDefined(keypress)}
            aria-label=${ifDefined(arialabel)}></aab-icon>`;
  }

  static handleKeyPress(evt, action, evtData) {
    if (evt.key === 'Enter' || evt.key === ' ') {
      AabTableCell.handleClick(evt, action, evtData);
    }
  }

  static handleClick(evt, action, evtData) {
    evt.stopImmediatePropagation();
    evt.currentTarget.dispatchEvent(new CustomEvent('icon-clicked', {
      detail: { name: action, data: evtData },
      bubbles: true,
      composed: true,
    }));
  }

  static renderLabel({
    message, background, type, icon, iconOutlined,
  }) {
    return html`<aab-inputmessage
      message=${ifDefined(message)}
      type=${ifDefined(type)}
      ?background=${background}
      ?icon=${icon || iconOutlined}></aab-inputmessage>`;
  }

  // The default template, display the given data
  static renderDefault(data) {
    let style = '';
    if (data && data.style && data.style instanceof Object) {
      style = `${Object.entries(data.style).reduce((acc, [key, val]) => `${acc}${key}:${val};`, '')}`;
    }
    return html`<div style="${style}" title="${ifDefined(data.title)}">${data.data ? data.data : data}</div>`;
  }

  static get renderFunctions() {
    return {
      account: AabTableCell.renderAccount,
      amount: AabTableCell.renderAmount,
      avatar: AabTableCell.renderAvatar,
      checkbox: AabTableCell.renderCheckbox,
      icon: AabTableCell.renderIcon,
      label: AabTableCell.renderLabel,
    };
  }

  static renderTemplate(template, data) {
    const renderFn = AabTableCell.renderFunctions[template];
    if (renderFn) {
      if (template === 'icon' && Array.isArray(data)) {
        return data.map((iconObj) => renderFn(iconObj));
      }
      return renderFn(data);
    }
    return AabTableCell.renderDefault(data);
  }

  render() {
    return html`<div class="table-cell d-flex align-items-center">${AabTableCell.renderTemplate(this.template, this.data)}</div>`;
  }
}
